/**
 * 지식 편집기 모달
 * 지식 생성 및 수정
 */

import React, { useState, useEffect } from 'react';
import { useKnowledge } from '../../contexts/KnowledgeContext';
import './KnowledgeEditor.css';

function KnowledgeEditor({ item, onClose }) {
  const { categories, createItem, updateItem } = useKnowledge();
  const isEditing = !!item;

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'general_notes',
    tags: [],
    priority: 5
  });
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // 수정 모드일 때 초기값 설정
  useEffect(() => {
    if (item) {
      setFormData({
        title: item.title || '',
        content: item.content || '',
        category: item.category || 'general_notes',
        tags: item.tags || [],
        priority: item.priority || 5
      });
    }
  }, [item]);

  // 입력 핸들러
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // 태그 추가
  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
      setTagInput('');
    }
  };

  // 태그 제거
  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // 태그 입력 키 핸들러
  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  // 저장 핸들러
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      setError('제목을 입력해주세요.');
      return;
    }

    if (!formData.content.trim()) {
      setError('내용을 입력해주세요.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (isEditing) {
        await updateItem(item.id, formData);
      } else {
        await createItem(formData);
      }
      onClose();
    } catch (err) {
      console.error('Save failed:', err);
      setError('저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="knowledge-editor" onClick={e => e.stopPropagation()}>
        <div className="editor-header">
          <h2>{isEditing ? '지식 수정' : '새 지식 추가'}</h2>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          {error && <div className="error-message">{error}</div>}

          {/* 제목 */}
          <div className="form-group">
            <label htmlFor="title">제목 *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="제목을 입력하세요"
              autoFocus
            />
          </div>

          {/* 카테고리 */}
          <div className="form-group">
            <label htmlFor="category">카테고리 *</label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleChange}
            >
              {Object.entries(categories).map(([key, cat]) => (
                <option key={key} value={key}>
                  {cat.icon} {cat.label}
                </option>
              ))}
            </select>
          </div>

          {/* 내용 */}
          <div className="form-group">
            <label htmlFor="content">내용 * (Markdown 지원)</label>
            <textarea
              id="content"
              name="content"
              value={formData.content}
              onChange={handleChange}
              placeholder="내용을 입력하세요&#10;&#10;Markdown 문법을 사용할 수 있습니다:&#10;- **굵게**, *기울임*&#10;- # 제목&#10;- - 목록&#10;- `코드`"
              rows={10}
            />
          </div>

          {/* 태그 */}
          <div className="form-group">
            <label>태그 (검색 키워드)</label>
            <div className="tag-input-wrapper">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                placeholder="태그 입력 후 Enter"
              />
              <button type="button" onClick={addTag} className="btn-add-tag">
                +
              </button>
            </div>
            {formData.tags.length > 0 && (
              <div className="tags-list">
                {formData.tags.map((tag, idx) => (
                  <span key={idx} className="tag">
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="tag-remove"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* 우선순위 */}
          <div className="form-group">
            <label htmlFor="priority">
              우선순위: {formData.priority}
            </label>
            <input
              type="range"
              id="priority"
              name="priority"
              min="1"
              max="10"
              value={formData.priority}
              onChange={handleChange}
              className="priority-slider"
            />
            <div className="priority-labels">
              <span>낮음 (1)</span>
              <span>높음 (10)</span>
            </div>
          </div>

          {/* 버튼 */}
          <div className="form-actions">
            <button type="button" onClick={onClose} disabled={saving}>
              취소
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? '저장 중...' : (isEditing ? '수정' : '저장')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default KnowledgeEditor;
